import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnInit,
    signal,
    ViewEncapsulation,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { Contact } from "../../../../pojo/Contact";
import { S25Util } from "../../../../util/s25-util";
import { S25LoadingApi } from "../../../s25-loading/loading.api";
import { ContactService } from "../../../../services/contact.service";
import { S25ItemI } from "../../../../pojo/S25ItemI";
import { Proto } from "../../../../pojo/Proto";
import ServiceStatus = Proto.ServiceStatus;

@TypeManagerDecorator("s25-ng-editable-contact-orgs")
@Component({
    selector: "s25-ng-editable-contact-orgs",
    template: `
        @if (init()) {
            <s25-loading-inline [model]="{}"></s25-loading-inline>
            @for (org of orgs; track org.itemId) {
                <div class="org-wrapper c-margin-bottom--quarter">
                    @if (canEdit) {
                        <button class="c-textButton" (click)="update(org, 'del')">
                            <s25-ng-icon [type]="'close'"></s25-ng-icon>
                        </button>
                    }
                    <s25-item-organization
                        [modelBean]="{ itemId: +org.itemId }"
                        [includeTypeIcon]="true"
                    ></s25-item-organization>
                    @if (canEdit) {
                        <s25-ng-dropdown-search-criteria
                            [type]="'organizationRoles'"
                            [(chosen)]="org.role"
                            (chosenChange)="update(org, 'mod')"
                        ></s25-ng-dropdown-search-criteria>
                    } @else {
                        <p class="ngItalic">{{ org.role.itemName }}</p>
                    }
                </div>
            }
            @if (canEdit) {
                <div class="add-org-wrapper c-margin-top--half">
                    <label class="ngBold">Add Organization</label>
                    <s25-ng-dropdown-search-criteria
                        [type]="'organizations'"
                        [(chosen)]="newOrg"
                        [ariaLabel]="'Add Organization'"
                        (chosenChange)="add()"
                    ></s25-ng-dropdown-search-criteria>
                </div>
                @if (duplicateOrgMsg()) {
                    <p class="ngFinePrint ngRed">{{ duplicateOrgMsg() }}</p>
                }
            }
        }
    `,
    styles: `
        @container contactorgs (width <= 600px) {
            .org-wrapper,
            .add-org-wrapper {
                gap: 5px !important;
                flex-wrap: wrap;

                s25-ng-dropdown-search-criteria {
                    flex-basis: 96% !important;
                    margin-bottom: 5px;
                }
            }

            .add-org-wrapper {
                margin-top: 0 !important;
                margin-left: 5px;

                s25-ng-dropdown-search-criteria {
                    flex-basis: 100% !important;
                }
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class S25EditableContactOrgsComponent implements OnInit {
    @Input() itemId: number;
    @Input() canEdit: boolean;
    @Input() orgs: Contact.DetailData["editableOrgs"];

    init = signal(false);
    duplicateOrgMsg = signal("");
    newOrg: S25ItemI & { status: string };

    constructor(
        private el: ElementRef,
        private cd: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.orgs = S25Util.array.forceArray(this.orgs);
        this.orgs.length > 1 && this.orgs.sort(S25Util.shallowSort("itemName"));
        this.init.set(true);
    }

    async save() {
        S25LoadingApi.init(this.el.nativeElement);
        const payload = this.orgs.map((org) => {
            const { itemId, itemName, role } = org;
            return {
                status: org.status ?? "est",
                organization_id: itemId,
                organization_name: itemName,
                role_id: role?.itemId,
                role_name: role?.itemName,
            };
        });
        const [_, error] = await S25Util.Maybe(
            ContactService.putContactMinimal(this.itemId, { organization: payload }),
        );
        S25LoadingApi.destroy(this.el.nativeElement);
        if (error) return S25Util.showError(error);
        S25Util.array.inplaceRemoveByProp(this.orgs, "status", "del");
        this.cd.markForCheck();
        this.orgs.forEach((org) => {
            if (["new", "mod"].includes(org.status)) {
                org.status = "est";
            }
        });
    }

    add() {
        this.duplicateOrgMsg.update((prev) => prev && "");
        this.newOrg.status = "new";
        if (S25Util.array.inplacePushByProp(this.orgs, "itemId", this.newOrg.itemId, this.newOrg)) {
            this.newOrg = null;
            return this.save();
        }
        this.duplicateOrgMsg.set(`This contact is already associated with ${this.newOrg.itemName}`);
    }

    update(org: Contact.DetailData["editableOrgs"][number], status: ServiceStatus) {
        org.status = status;
        return this.save();
    }
}
